<template>
  <div>
    <!-- search -->
    <search-nav @handleSearch="onClickSearch">
      <div class="search-layout w-full grid col-gap-4 flex-wrap xl:flex">
        <component
          v-for="(component, index) in stdSearchComponents"
          v-if="showSearchNavItem(component['options'])"
          :is="component['component']"
          :label="component['label']"
          :query="component['query']"
          :options="component['options']"
          :onClickSearch="onClickSearch"
          v-model="nav.value[component['value']]"
        />
      </div>
    </search-nav>

    <div class="flex items-center justify-between mb-4">
      <vs-select v-model="perPage">
        <vs-select-item text="5개씩 보기" :value="5" />
        <vs-select-item text="10개씩 보기" :value="10" />
        <vs-select-item text="20개씩 보기" :value="20" />
        <vs-select-item text="50개씩 보기" :value="50" />
      </vs-select>
      <vs-button color="secondary" type="border" @click="onClickDownload">
        Excel 다운로드
      </vs-button>
    </div>

    <!-- Data -->
    <table>
      <colgroup>
        <col style="width: 44px" />
        <col style="width: 90px" />
        <col style="width: 130px" />
        <col />
        <col style="width: 200px" />
        <col style="width: 100px" />
      </colgroup>
      <thead>
        <tr>
          <th class="col-toggle"></th>
          <th>대상년도</th>
          <th>사업장명</th>
          <th class="sortable" @click="toggleSort('사외수급업체명')">
            사외 수급업체명
            <span class="sort-icon">{{ sort.field === '사외수급업체명' ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅' }}</span>
          </th>
          <th class="sortable" @click="toggleSort('업체코드')">
            업체코드
            <span class="sort-icon">{{ sort.field === '업체코드' ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅' }}</span>
          </th>
          <th class="sortable" @click="toggleSort('평균점수')">
            평균점수
            <span class="sort-icon">{{ sort.field === '평균점수' ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅' }}</span>
          </th>
        </tr>
      </thead>
      <tbody v-for="group in paginatedGroupedList" :key="group.key">
        <tr class="group-row" @click="toggleGroup(group.key)">
          <td class="col-toggle">
            <span class="toggle-icon" :class="{ expanded: !!expandedGroups[group.key] }">&#9654;</span>
          </td>
          <td>{{ group.대상년도 }}</td>
          <td>{{ group.사업장명 }}</td>
          <td>{{ group.사외수급업체명 }}</td>
          <td>{{ group.업체코드 }}</td>
          <td>{{ group.평균점수 != null ? group.평균점수.toFixed(1) : '-' }}</td>
        </tr>
        <tr v-if="expandedGroups[group.key]" class="detail-row">
          <td colspan="6">
            <table class="inner-table">
              <colgroup>
                <col />
                <col style="width: 130px" />
                <col style="width: 200px" />
                <col style="width: 200px" />
                <col style="width: 100px" />
              </colgroup>
              <thead>
                <tr>
                  <th class="text-right">공사명</th>
                  <th>구분</th>
                  <th>작업기간</th>
                  <th>총점</th>
                  <th>평균점수</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(child, idx) in group.items" :key="idx" :class="child.구분 === '최종평가 완료' ? 'row-final' : 'row-doc'">
                  <td class="text-right">{{ child.공사명 }}</td>
                  <td>{{ child.구분 }}</td>
                  <td>{{ child.작업기간 ? (child.작업기간.sDate || '') + ' ~ ' + (child.작업기간.eDate || '') : '-' }}</td>
                  <td>{{ child.총점 }}</td>
                  <td v-if="child.isFirstInGroup" :rowspan="child.groupRowspan">{{ child.groupAverage != null ? child.groupAverage.toFixed(1) : '-' }}</td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="vs-con-table">
      <div class="con-pagination-table vs-table--pagination">
        <k-pagination :total="lastPage" v-model="page"></k-pagination>
      </div>
    </div>

    <popup-excel-download v-model="download" :list="excelList" title="사외 수급업체 평균점수 현황" />
  </div>
</template>

<script>
import SearchNav from "@/components/nav/SearchNav.vue";
import SearchSelect from "@/views/list/components/SearchSelect.vue";
import SearchColumnKeyword from "@/views/list/components/SearchColumnKeyword.vue";
import FieldSelector from "@/components/selector/FieldSelector.vue";
import List from "./List.js";
import searchComponent from "@/views/list/components/include";
import KPagination from "@/components/kPagination/kPagination.vue";
import PopupExcelDownload from "@/popup/PopupExcelDownload.vue";

export default {
  components: {
    KPagination,
    FieldSelector,
    SearchColumnKeyword,
    SearchNav,
    SearchSelect,
    PopupExcelDownload,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    header: Object,
    value: Object,
    mode: {
      type: String,
      default: "list",
    },
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    boardId() {
      return 897;
    },
    stdSearchComponents() {
      const stdSearchComponents = [
        {
          component: "search-year",
          label: "대상년도",
          value: "year",
          query: { y: "year" },
          options: {
            default: new Date().getFullYear(),
          },
        },
        {
          component: "search-select",
          label: "사업장명",
          value: "사업장명",
          query: { f: "사업장명" },
          options: {
            default: "",
            items: this.사업장명Items,
          },
        },
        {
          component: "search-start-end",
          label: "작업기간",
          value: "date",
          query: { ss: "startDate", ee: "endDate" },
          options: {
            default: { sd: "", ed: "" },
          },
        },
        {
          component: "search-keyword",
          label: "수급업체명",
          value: "수급업체명",
          query: { k: "수급업체명" },
          options: {
            default: "",
          },
        },
        {
          component: "search-select",
          label: "구분",
          value: "구분",
          query: { t: "구분" },
          options: {
            items: [
              { text: "전체", value: "" },
              { text: "서류평가 완료", value: "서류평가완료" },
              { text: "최종평가 완료", value: "최종평가완료" },
            ],
            default: "",
          },
        },
      ];

      for (const component of stdSearchComponents) {
        component.component = searchComponent[component["component"]];
      }

      return stdSearchComponents;
    },

    // 사업장명 선택 드롭다운 아이템 목록
    사업장명Items() {
      const seen = new Set();
      const items = [];
      for (const item of this.allProcessedList) {
        if (item.사업장명 && !seen.has(item.사업장명)) {
          seen.add(item.사업장명);
          items.push({ text: item.사업장명, value: item.사업장명 });
        }
      }
      items.sort((a, b) => a.value ? a.text.localeCompare(b.text) : -1);
      items.unshift({ text: "전체", value: "" });
      return items;
    },

    // 전체 원시 데이터 → 처리된 항목 목록
    allProcessedList() {
      return this.allRawData.map((data) => {
        const content = data.postContents;
        let isFinish = false;
        if (content.body.evaluationList && content.body.evaluationList[1]) {
          const childs = content.body.evaluationList[1].childs;
          isFinish = childs.some((child) => Number(child.value) > 0);
        }
        return {
          id: `${data.id}`,
          대상년도: content.header.year,
          구분: isFinish ? "최종평가 완료" : "서류평가 완료",
          사업장명: content.body.stdInfo.field_name,
          사외수급업체명: content.body.업체명.com_name,
          업체코드: "-",
          공사명: content.body.구분.공사명,
          작업기간: (content.body.구분 && content.body.구분.작업기간) ? content.body.구분.작업기간 : null,
          총점: content.body.userScore,
          업체id: content.body.업체명.id,
        };
      });
    },

    // 해당 년도 전체 데이터 기준 업체별 평균점수 맵 (최종평가 완료만 집계)
    averageScoreMap() {
      const map = {};
      for (const item of this.allProcessedList) {
        if (item.구분 !== '최종평가 완료') continue;
        const companyId = item.업체id;
        const score = Number(item.총점);
        if (map[companyId]) {
          map[companyId].sum += score;
          map[companyId].count += 1;
          map[companyId].average = map[companyId].sum / map[companyId].count;
        } else {
          map[companyId] = { sum: score, count: 1, average: score };
        }
      }
      return map;
    },

    // 클라이언트 사이드 필터링 (대상년도 제외)
    filteredList() {
      const search = this.nav.value;
      let list = this.allProcessedList;

      if (search.date) {
        if (search.date.startDate) {
          list = list.filter(
            (item) => item.작업기간 && item.작업기간.eDate >= search.date.startDate
          );
        }
        if (search.date.endDate) {
          const nextDay = new Date(search.date.endDate).addDays(1).format("yyyy-MM-dd");
          list = list.filter(
            (item) => item.작업기간 && item.작업기간.sDate < nextDay
          );
        }
      }

      if (search.사업장명) {
        list = list.filter((item) => item.사업장명 === search.사업장명);
      }

      if (search.수급업체명) {
        const kw = search.수급업체명.toLowerCase();
        list = list.filter((item) => item.사외수급업체명.toLowerCase().includes(kw)
        );
      }

      if (search.구분 === "서류평가완료") {
        list = list.filter((item) => item.구분 === "서류평가 완료");
      } else if (search.구분 === "최종평가완료") {
        list = list.filter((item) => item.구분 === "최종평가 완료");
      }

      return list;
    },

    // 업체별 그룹핑 (구분별 정렬 및 rowspan 정보 포함)
    groupedList() {
      const groups = {};
      for (const item of this.filteredList) {
        const key = item.업체id;
        if (!groups[key]) {
          groups[key] = {
            key,
            대상년도: item.대상년도,
            사업장명: item.사업장명,
            사외수급업체명: item.사외수급업체명,
            업체코드: item.업체코드,
            평균점수: null,
            items: [],
          };
        }
        groups[key].items.push({
          구분: item.구분,
          공사명: item.공사명,
          작업기간: item.작업기간,
          총점: item.총점,
        });
      }

      for (const group of Object.values(groups)) {
        // 구분별 정렬 (최종평가 완료 → 서류평가 완료 순)
        group.items.sort((a, b) => {
          if (a.구분 === b.구분) return 0;
          return a.구분 === '최종평가 완료' ? -1 : 1;
        });

        // 구분별 합계/건수/평균 계산
        const 구분Map = {};
        for (const item of group.items) {
          if (!구분Map[item.구분]) 구분Map[item.구분] = { sum: 0, count: 0 };
          구분Map[item.구분].sum += Number(item.총점);
          구분Map[item.구분].count += 1;
        }
        for (const g of Object.values(구분Map)) {
          g.average = g.sum / g.count;
        }

        // 외부 행: 최종평가 완료 평균만 표시
        const finalData = 구분Map['최종평가 완료'];
        group.평균점수 = finalData ? finalData.average : null;

        // 각 item에 구분별 rowspan 정보 부여
        let currentGroup = null;
        for (const item of group.items) {
          if (item.구분 !== currentGroup) {
            currentGroup = item.구분;
            item.isFirstInGroup = true;
            item.groupRowspan = 구분Map[item.구분].count;
            item.groupAverage = 구분Map[item.구분].average;
          } else {
            item.isFirstInGroup = false;
          }
        }
      }

      return Object.values(groups);
    },

    // 정렬 적용
    processedGroupedList() {
      let list = this.groupedList;

      if (this.sort.field) {
        const { field, dir } = this.sort;
        list = [...list].sort((a, b) => {
          const va = a[field];
          const vb = b[field];
          if (va == null && vb == null) return 0;
          if (va == null) return 1;
          if (vb == null) return -1;
          if (typeof va === 'string') return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
          return dir === 'asc' ? va - vb : vb - va;
        });
      }

      return list;
    },

    // 전체 페이지 수 (업체 기준)
    lastPage() {
      return Math.max(1, Math.ceil(this.processedGroupedList.length / this.perPage));
    },

    // 현재 페이지에 표시할 그룹 목록
    paginatedGroupedList() {
      const start = (this.page - 1) * this.perPage;
      return this.processedGroupedList.slice(start, start + this.perPage);
    },

    // 엑셀 다운로드용 목록 (전체 필터링 결과, 페이지 구분 없음)
    excelList() {
      return this.filteredList.map((item) => {
        const avgData = this.averageScoreMap[item.업체id];
        const 평균점수 = avgData ? Math.round(avgData.average * 10) / 10 : null;
        const 작업기간 = item.작업기간
          ? `${item.작업기간.sDate || ""  } ~ ${  item.작업기간.eDate || ""}`
          : "-";
        return {
          대상년도: item.대상년도,
          구분: item.구분,
          사업장명: item.사업장명,
          사외수급업체명: item.사외수급업체명,
          업체코드: item.업체코드,
          공사명: item.공사명,
          작업기간,
          총점: item.총점,
          평균점수,
        };
      });
    },
  },
  data() {
    return {
      nav: {
        value: {
          year: new Date().getFullYear(),
        },
      },

      allRawData: [],

      page: 1,
      perPage: 10,

      sort: { field: null, dir: 'desc' },

      download: false,
      expandedGroups: {},
    };
  },
  watch: {
    "nav.value.year": {
      immediate: true,
      handler() {
        this.loadAllFromApi();
      },
    },
    perPage() {
      this.page = 1;
    },
    lastPage(val) {
      if (this.page > val) this.page = val;
    },
  },
  methods: {
    showSearchNavItem(options) {
      if (options && options.visibleKeyValue) {
        for (const keyValue of options.visibleKeyValue) {
          const key = Object.keys(keyValue)[0];
          const value = keyValue[key];
          const navValue = this.findJsonDataWithDottedPath(this.nav.value, key);
          if (navValue !== value) {
            if (Array.isArray(navValue) && navValue.toString() === value) {
              return true;
            }
          } else {
            return true;
          }
        }
        return false;
      }
      return true;
    },

    onClickSearch() {
      this.page = 1;
    },

    onClickDownload() {
      this.download = true;
    },

    toggleGroup(key) {
      this.$set(this.expandedGroups, key, !this.expandedGroups[key]);
    },

    toggleSort(field) {
      if (this.sort.field === field) {
        if (this.sort.dir === 'desc') {
          this.sort.dir = 'asc';
        } else {
          // asc → 해제
          this.sort.field = null;
          this.sort.dir = 'desc';
        }
      } else {
        this.sort.field = field;
        this.sort.dir = 'desc';
      }
      this.page = 1;
    },

    // 대상년도 변경 시에만 API 호출 (200개 단위 청크로 전체 로드)
    async loadAllFromApi() {
      const year = this.nav.value.year;
      const baseOptions = {
        projectId: this.projectId,
        boardId: this.boardId,
        perPage: 200,
        conditions: [{ key: "header.year", operator: "=", value: `${year}` }],
        withMeta: true,
        withoutRelation: true,
        onlyMeta: true,
        metaKeys: [
          "body.stdInfo.field_name",
          "header.year",
          "body.업체명.com_name",
          "body.업체명.id",
          "body.구분.공사명",
          "body.구분.작업기간%",
          "body.userScore",
          "body.evaluationList[1].childs%",
        ],
        orderTarget: "$body.업체명.com_name",
        orderDirection: "desc",
      };

      const firstResponse = await this.$store.dispatch("board/LOAD_POST_LIST", {
        ...baseOptions,
        page: 1,
      });
      const apiLastPage = firstResponse.last_page;
      let allRawData = [...firstResponse.data];

      if (apiLastPage > 1) {
        const pagePromises = [];
        for (let p = 2; p <= apiLastPage; p++) {
          pagePromises.push(
            this.$store.dispatch("board/LOAD_POST_LIST", { ...baseOptions, page: p })
          );
        }
        const remainingResponses = await Promise.all(pagePromises);
        for (const res of remainingResponses) {
          allRawData = allRawData.concat(res.data);
        }
      }

      this.allRawData = allRawData;
      this.page = 1;
    },
  },
};
</script>
<style lang="scss" scoped>
table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #cdcdcd;
    padding: 16px 7px;
  }

  th {
    font-size: 14px;
    font-weight: 500;
    text-align: center;
    background: #f9f9f9;
  }

  td {
    font-size: 14px;
    font-weight: 400;
    text-align: center;
    background: white;
  }

  .col-toggle {
    width: 44px;
    padding: 0;
  }

  .sortable {
    cursor: pointer;
    user-select: none;
    white-space: nowrap;

    &:hover {
      background: #ececec;
    }
  }

  .sort-icon {
    display: inline-block;
    margin-left: 4px;
    font-size: 11px;
    color: #999;
    vertical-align: middle;
  }

  // 그룹 사이 구분선: 각 tbody의 첫 번째 tr 상단에 두꺼운 선
  tbody + tbody .group-row td {
    border-top: 2px solid #b0b8c4;
  }

  .group-row {
    cursor: pointer;

    td {
      background: #f0f2f5;
      font-weight: 500;
    }

    // 왼쪽 accent 테두리: 첫 번째 td에 box-shadow로 구현
    td:first-child {
      box-shadow: inset 4px 0 0 rgba(var(--vs-primary), 1);
    }

    &:hover td {
      background: rgba(var(--vs-primary), 0.1);
    }
  }

  .toggle-icon {
    display: inline-block;
    font-size: 11px;
    color: #666;
    transition: transform 0.2s ease;

    &.expanded {
      transform: rotate(90deg);
    }
  }

  .detail-row {
    > td {
      padding: 0;
      background: #f7f8fa;
      border-top: none;
      // 왼쪽 accent 테두리 연결
      box-shadow: inset 4px 0 0 rgba(var(--vs-primary), 0.35);
    }

    &:hover td {
      background: #f7f8fa;
    }
  }
}

.inner-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #e0e0e0;
    padding: 10px 12px;
    font-size: 14px;
  }

  th {
    background: #eef0f4;
    font-weight: 500;
    text-align: center;
  }

  td {
    background: #eee;
    text-align: center;
  }

  td.text-right,
  th.text-right {
    text-align: right;
  }

  .row-doc td {
    background: #f3f4f7;
    color: #555;
  }

  .row-final td {
    background: #eaf0fb;
    color: #2c4a8e;
  }

  tbody tr:hover td {
    background: rgba(var(--vs-primary), 0.08);
    color: inherit;
  }
}
</style>
